import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Logo from '../components/Logo';

export default function SignUp() {
  const { signUp } = useAuth();
  const navigate   = useNavigate();
  const [name, setName]         = useState('');
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [loadMsg, setLoadMsg]   = useState('Creating account…');
  const t1 = useRef(null);
  const t2 = useRef(null);

  const startTimers = () => {
    t1.current = setTimeout(() => setLoadMsg('Setting up your account…'), 3000);
    t2.current = setTimeout(() => setLoadMsg('Almost there — please wait…'), 7000);
  };
  const clearTimers = () => { clearTimeout(t1.current); clearTimeout(t2.current); };
  useEffect(() => () => clearTimers(), []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    setLoadMsg('Creating account…');
    startTimers();

    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      clearTimers();
      setLoading(false);
      return;
    }

    try {
      const data = await signUp(email, password, name);

      // Fire GTM sign_up event
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'sign_up',
        transaction_id: Date.now().toString(),
        method: 'email',
      });

      // Send welcome email in background — don't block navigation
      fetch('/api/welcome', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ name, email, plan: 'free' }),
      }).catch(err => console.log('Welcome email error:', err));

      // If Supabase returned a session immediately (email confirmation OFF)
      // send new user to plan selection screen
      if (data?.session) {
        navigate('/plans');
      } else {
        // Email confirmation is ON — user needs to check inbox first
        navigate('/confirm-email', { state: { email } });
      }
    } catch (err) {
      const msg = err.message || '';
      if (msg.includes('already registered') || msg.includes('already been registered')) {
        setError('An account with this email already exists. Try signing in instead.');
      } else {
        setError(msg || 'Something went wrong. Please try again.');
      }
    }
    clearTimers();
    setLoading(false);
    setLoadMsg('Creating account…');
  };

  return (
    <div style={s.page}>
      <div style={s.card}>
        <LogoHeader />
        <div style={s.freeBadge}>🆓 Free plan — no credit card needed</div>
        <h1 style={s.title}>Create your free account</h1>
        <p style={s.sub}>Start with the free plan. Upgrade any time.</p>

        {error && <div style={s.errorBox}>{error}</div>}

        <form onSubmit={handleSubmit} style={s.form}>
          <Field label="Your name">
            <input style={s.input} type="text" placeholder="e.g. Angela" value={name} onChange={e => setName(e.target.value)} required />
          </Field>
          <Field label="Email address">
            <input style={s.input} type="email" placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)} required />
          </Field>
          <Field label="Choose a password">
            <input style={s.input} type="password" placeholder="At least 6 characters" value={password} onChange={e => setPassword(e.target.value)} required />
          </Field>
          <button style={{ ...s.btn, opacity: loading ? 0.85 : 1, cursor: loading ? 'not-allowed' : 'pointer' }} type="submit" disabled={loading}>
            {loading ? (
              <span style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:10 }}>
                <span style={{ display:'inline-block', width:16, height:16, border:'2px solid #ffffff60', borderTop:'2px solid #fff', borderRadius:'50%', animation:'spin 0.8s linear infinite' }} />
                {loadMsg}
              </span>
            ) : 'Create Free Account →'}
          </button>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </form>

        <p style={s.terms}>
          By signing up you agree to our{' '}
          <Link to="/terms" style={s.link}>Terms</Link> and{' '}
          <Link to="/privacy" style={s.link}>Privacy Policy</Link>.
        </p>
        <p style={s.footer}>
          Already have an account?{' '}
          <Link to="/signin" style={s.link}>Sign in</Link>
        </p>
      </div>
    </div>
  );
}

function LogoHeader() {
  return (
    <div style={{ marginBottom:20 }}>
      <Logo size={36} showText={true} textSize={18} />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:7 }}>
      <label style={{ fontSize:13, fontWeight:700, color:'#334155', fontFamily:'sans-serif' }}>{label}</label>
      {children}
    </div>
  );
}

const s = {
  page:      { minHeight:'100vh', background:'linear-gradient(160deg,#f8faff,#eef2ff)', display:'flex', alignItems:'center', justifyContent:'center', padding:24 },
  card:      { background:'#fff', borderRadius:20, border:'1px solid #e2e8f0', padding:'40px 36px', width:'100%', maxWidth:440, boxShadow:'0 4px 40px rgba(99,102,241,0.08)' },
  freeBadge: { display:'inline-block', fontSize:12, fontWeight:700, color:'#059669', background:'#dcfce7', border:'1px solid #bbf7d0', padding:'5px 14px', borderRadius:20, fontFamily:'sans-serif', marginBottom:16 },
  title:     { fontSize:24, fontWeight:700, color:'#0f172a', marginBottom:8 },
  sub:       { fontSize:14, color:'#64748b', fontFamily:'sans-serif', marginBottom:24, lineHeight:1.5 },
  errorBox:  { background:'#fff1f2', border:'1px solid #fecdd3', borderRadius:10, padding:'12px 14px', fontSize:13, color:'#be123c', fontFamily:'sans-serif', marginBottom:18, lineHeight:1.5 },
  form:      { display:'flex', flexDirection:'column', gap:18 },
  input:     { padding:'13px 14px', border:'1.5px solid #e2e8f0', borderRadius:10, fontSize:14, color:'#1e293b', outline:'none', fontFamily:'sans-serif', background:'#fafbff' },
  btn:       { padding:'14px', borderRadius:10, background:'linear-gradient(135deg,#6366f1,#818cf8)', color:'#fff', border:'none', fontSize:15, fontWeight:700, cursor:'pointer', fontFamily:'sans-serif', marginTop:4 },
  terms:     { textAlign:'center', fontSize:12, color:'#94a3b8', fontFamily:'sans-serif', marginTop:16, lineHeight:1.5 },
  footer:    { textAlign:'center', fontSize:13, color:'#94a3b8', fontFamily:'sans-serif', marginTop:12 },
  link:      { color:'#6366f1', fontWeight:600, textDecoration:'none' },
};